import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { FormControl, FormHelperText, TextField, Typography } from "@mui/material"
import { CURRENCIES } from "currency/model"
import { checkTouched, isTouched as hasBeenTouched } from "form/tools/validation"
import { Field, FieldProps } from "formik"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { NumberInput } from "./base/NumberInput"

interface IFieldMoneyProps {
  currencyId: CURRENCIES
  fieldName: string // ex: "main.order_id"
  label: string
  disabled?: boolean
  isReadOnly?: boolean
  required?: boolean
  value: string | number | null
  placeholder?: string
  error?: string
  clearable?: boolean
  sx?: any
  sxField?: any
  onBoxClick?: (...args: any) => any // only used for ui config preview
  onTextClick?: (...args: any) => any // only used for ui config preview
}

export const FieldMoney = React.memo(
  ({
    currencyId,
    fieldName,
    label,
    disabled,
    required,
    isReadOnly,
    value,
    placeholder,
    error,
    clearable,
    sx,
    sxField,
    onBoxClick,
    onTextClick
  }: IFieldMoneyProps) => {
    const timeout = React.useRef<any>(undefined)
    const [val, setVal] = React.useState(typeof value === "number" ? Math.round(value) : value)
    return (
      <Field name={fieldName}>
        {({ form: { touched, setFieldValue } }: FieldProps) => {
          const isTouched: boolean = hasBeenTouched(touched, fieldName)
          const isDisabled: boolean = disabled || Boolean(isReadOnly)
          const hasError = isTouched ? error !== undefined && error !== "" : false
          return (
            <FormControl
              id={fieldName}
              error={hasError}
              className="field-money"
              sx={{
                "display": "flex",
                "flexDirection": "row",
                "flexWrap": "wrap",
                "alignItems": "center",
                "& fieldset": {
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderLeft: "none"
                },
                "& .icon": {
                  cursor: "pointer",
                  color: theme => theme.palette.text.secondary
                },
                "& .icon:hover": {
                  color: theme => theme.palette.text.primary
                },
                "& input": { paddingRight: clearable ? "1.4rem" : undefined },
                "& input::placeholder": { fontSize: ".7rem" },
                ...sx
              }}>
              <FlexBox sx={{ ...sxField }}>
                <FlexBox
                  className="field-money-currency"
                  onClick={onBoxClick}
                  sx={{
                    backgroundColor: theme => theme.palette.form,
                    opacity: isDisabled ? 0.6 : 1,
                    pl: ".5rem",
                    pr: ".5rem",
                    borderTopLeftRadius: ".25rem",
                    borderBottomLeftRadius: ".25rem",
                    height: "2.5rem",
                    transition: "opacity 400ms ease"
                  }}>
                  <Typography
                    onClick={onTextClick}
                    sx={{
                      color: theme => theme.palette.formText,
                      fontSize: ".8rem"
                    }}>
                    {currencyId}
                  </Typography>
                </FlexBox>
                <TextField
                  id={`${fieldName}-text-field`}
                  name={fieldName}
                  variant="outlined"
                  error={hasError}
                  required={required}
                  label={label}
                  size="small"
                  value={val === null ? "" : val}
                  // type="number" don't uncomment, else it will break NumberInput
                  placeholder={placeholder}
                  onChange={event => {
                    clearTimeout(timeout.current)
                    checkTouched(touched, fieldName)
                    const newValue = event.target.value !== "" ? Number(event.target.value) : null
                    setVal(newValue)
                    timeout.current = setTimeout(() => setFieldValue(fieldName, newValue), 500)
                  }}
                  onBlur={() => {
                    if (!isTouched) {
                      checkTouched(touched, fieldName)
                      setFieldValue(fieldName, value)
                    }
                  }}
                  InputProps={{
                    inputComponent: NumberInput as any,
                    endAdornment:
                      clearable && value !== null ? (
                        <FontAwesomeIcon
                          id={`${fieldName}-clear-button`}
                          icon={faXmark}
                          className="icon"
                          onClick={() => {
                            setVal(null)
                            checkTouched(touched, fieldName)
                            setFieldValue(fieldName, null)
                          }}
                        />
                      ) : undefined
                  }}
                  disabled={isDisabled}
                />
              </FlexBox>
              {hasError && <FormHelperText id={`${fieldName}-error`}>{error}</FormHelperText>}
            </FormControl>
          )
        }}
      </Field>
    )
  }
)
